/**
 * OCSubmissionTab.js
 * "OC Submission" tab for ProjectDetail — container for OC-related forms.
 *
 * Data flow:
 *   - OC submission data → fetched/saved via GET|PATCH /api/oc-submissions/{project.id}
 *   - Each form is stored under its own key (e.g. builders_declaration), so
 *     additional forms can be added incrementally without changing this container.
 *
 * Autosave: debounce 1s on any field change → PATCH /api/oc-submissions/{project.id}
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import BuildersDeclarationForm from './oc/BuildersDeclarationForm';
import PortableFireInstallationForm from './oc/PortableFireInstallationForm';
import AccessCertificateForm from './oc/AccessCertificateForm';
import ExitEmergencyLightCertificateForm from './oc/ExitEmergencyLightCertificateForm';
import ElectricalComplianceCertificateForm from './oc/ElectricalComplianceCertificateForm';
import FloorResistanceComplianceCertificateForm from './oc/FloorResistanceComplianceCertificateForm';
import PlumbingGasComplianceCertificateForm from './oc/PlumbingGasComplianceCertificateForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMS = [
  { key: 'builders_declaration', label: "Builder's Declaration" },
  { key: 'portable_fire_installation', label: 'Portable Fire Installation' },
  { key: 'access_certificate', label: 'Access Certificate' },
  { key: 'exit_emergency_light_certificate', label: 'Exit & Emergency Light' },
  { key: 'electrical_compliance_certificate', label: 'Electrical Compliance' },
  { key: 'floor_resistance_compliance_certificate', label: 'Floor Resistance' },
  { key: 'plumbing_gas_compliance_certificate', label: 'Plumbing & Gas' },
];

// Fields shared across all OC forms — editing one syncs all others.
const SHARED_FIELDS = ['cdc_no', 'lot', 'dp', 'project_name', 'address', 'building_class'];

export default function OCSubmissionTab({ project }) {
  const [data, setData] = useState(null);
  const [activeForm, setActiveForm] = useState(FORMS[0].key);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});
  // Ref keeps current data available inside callbacks without stale-closure issues
  const dataRef = useRef(null);

  // Load OC submission document
  useEffect(() => {
    if (!project?.id) return;
    axios.get(`${API}/oc-submissions/${project.id}`)
      .then(res => setData(res.data))
      .catch(err => {
        console.error('OCSubmissionTab load error:', err);
        setData({ builders_declaration: {} });
      });
  }, [project?.id]);

  // Keep dataRef current so handleChange can read sibling form data without stale closures
  useEffect(() => { dataRef.current = data; }, [data]);

  // Debounced autosave
  const scheduleAutosave = useCallback((patch) => {
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const toSave = { ...pendingPatch.current };
      pendingPatch.current = {};
      setSaving(true);
      setSaveError(null);
      try {
        await axios.patch(`${API}/oc-submissions/${project.id}`, toSave);
      } catch (err) {
        console.error('OC submission autosave error:', err);
        setSaveError('Autosave failed');
      } finally {
        setSaving(false);
      }
    }, 1000);
  }, [project?.id]);

  // Field change handler — `field` is a top-level key (e.g. 'builders_declaration')
  // SHARED_FIELDS (project_name, address, cdc_no, lot, dp, building_class) are propagated to all other forms automatically.
  const handleChange = useCallback((field, value) => {
    // Collect shared-field values present in this update
    const sharedValues = {};
    SHARED_FIELDS.forEach(f => {
      if (value[f] !== undefined) sharedValues[f] = value[f];
    });
    const hasShared = Object.keys(sharedValues).length > 0;

    // Update state — apply shared values to all sibling forms
    setData(prev => {
      const next = { ...prev, [field]: value };
      if (hasShared) {
        FORMS.forEach(form => {
          if (form.key !== field) {
            next[form.key] = { ...(next[form.key] || {}), ...sharedValues };
          }
        });
      }
      return next;
    });

    // Build autosave patch — include full sibling form data with shared-field merge
    const autosavePatch = { [field]: value };
    if (hasShared) {
      const currentData = dataRef.current || {};
      FORMS.forEach(form => {
        if (form.key !== field) {
          autosavePatch[form.key] = { ...(currentData[form.key] || {}), ...sharedValues };
        }
      });
    }
    scheduleAutosave(autosavePatch);
  }, [scheduleAutosave]);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading OC Submission…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-nav + status */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {FORMS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveForm(f.key)}
              className="px-3 py-1.5 text-xs font-semibold rounded transition-colors"
              style={{
                background: activeForm === f.key ? '#1a1a2e' : 'transparent',
                color: activeForm === f.key ? '#c9a84c' : '#6b7280',
                border: '1px solid', borderColor: activeForm === f.key ? '#1a1a2e' : '#e5e4df',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">
          {saving ? 'Saving…' : saveError ? saveError : 'All changes saved'}
        </span>
      </div>

      {activeForm === 'builders_declaration' && (
        <BuildersDeclarationForm
          project={project}
          data={data.builders_declaration || {}}
          onChange={v => handleChange('builders_declaration', v)}
        />
      )}

      {activeForm === 'portable_fire_installation' && (
        <PortableFireInstallationForm
          project={project}
          data={data.portable_fire_installation || {}}
          onChange={v => handleChange('portable_fire_installation', v)}
        />
      )}

      {activeForm === 'access_certificate' && (
        <AccessCertificateForm
          project={project}
          data={data.access_certificate || {}}
          onChange={v => handleChange('access_certificate', v)}
        />
      )}

      {activeForm === 'exit_emergency_light_certificate' && (
        <ExitEmergencyLightCertificateForm
          project={project}
          data={data.exit_emergency_light_certificate || {}}
          onChange={v => handleChange('exit_emergency_light_certificate', v)}
        />
      )}

      {activeForm === 'electrical_compliance_certificate' && (
        <ElectricalComplianceCertificateForm
          project={project}
          data={data.electrical_compliance_certificate || {}}
          onChange={v => handleChange('electrical_compliance_certificate', v)}
        />
      )}

      {activeForm === 'floor_resistance_compliance_certificate' && (
        <FloorResistanceComplianceCertificateForm
          project={project}
          data={data.floor_resistance_compliance_certificate || {}}
          onChange={v => handleChange('floor_resistance_compliance_certificate', v)}
        />
      )}

      {activeForm === 'plumbing_gas_compliance_certificate' && (
        <PlumbingGasComplianceCertificateForm
          project={project}
          data={data.plumbing_gas_compliance_certificate || {}}
          onChange={v => handleChange('plumbing_gas_compliance_certificate', v)}
        />
      )}
    </div>
  );
}
